import { signageAgent } from './signage-agent';

export interface SignagePairingRequestCreatedResponse {
  requestId: string;
  expiresAt: string;
  pollIntervalMs: number;
}

type SignagePairingStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'EXPIRED' | 'CANCELLED';

export interface SignagePairingRequestPollResponse {
  status: SignagePairingStatus;
  deviceType?: string;
  deviceToken?: string;
  companyId?: string;
  companyName?: string;
  officeId?: number;
  officeLabel?: string;
}

export interface SignageFloor {
  id: number;
  label: string;
  order: number;
}

export interface SignageDesk {
  id: number;
  label: string;
  x: number | null;
  y: number | null;
  width: number | null;
  height: number | null;
  rotation: number | null;
  active: boolean;
}

export interface SignageArea {
  id: number;
  label: string;
  x: number;
  y: number;
  width: number | null;
  height: number | null;
}

export interface SignageMeetingRoom {
  id: string;
  name: string;
  capacity: number;
  x: number;
  y: number;
  width: number | null;
  height: number | null;
}

export interface SignageFloorPlan {
  floorId: number;
  label: string;
  officePlan: string | null;
  officeFloorPlan: string | null;
  desks: SignageDesk[];
  areas: SignageArea[];
  meetingRooms: SignageMeetingRoom[];
}

export type SignageNameDisplay = 'FULL_NAMES' | 'INITIALS' | 'RESERVED_ONLY';

export interface SignageOccupant {
  displayName: string | null;
  initials: string | null;
  firstAider: boolean;
  fireWarden: boolean;
}

export interface SignageDeskOccupancy {
  deskId: number;
  am: SignageOccupant | null;
  pm: SignageOccupant | null;
}

export interface SignageMeetingRoomBusyBlock {
  start: string;
  end: string;
}

export interface SignageMeetingRoomOccupancy {
  meetingRoomId: string;
  busy: SignageMeetingRoomBusyBlock[];
}

export interface SignageSafetyPerson {
  name: string;
  // Floor the person is sitting on today; null when it can't be determined
  floorId: number | null;
  floorLabel: string | null;
}

export interface SignageSafetyRoles {
  enabled: boolean;
  firstAiders: SignageSafetyPerson[];
  fireWardens: SignageSafetyPerson[];
}

export interface SignageOccupancy {
  date: string;
  timezone: string;
  nameDisplay: SignageNameDisplay;
  refreshIntervalMs: number;
  officeLabel: string;
  // Logo filename under /assets/images/logos/, per CompanyEntity.logoUrl
  companyLogoUrl: string | null;
  floorId: number;
  desks: SignageDeskOccupancy[];
  desksTotal: number;
  desksInUseAm: number;
  desksInUsePm: number;
  alsoInToday: SignageOccupant[];
  alsoInTodayCount: number;
  peopleInTodayCount: number;
  safety: SignageSafetyRoles;
  meetingRooms: SignageMeetingRoomOccupancy[];
}

export type SignageAwayCategory = 'OUT_OF_OFFICE' | 'WORKING_ELSEWHERE';

export interface SignageAwayHalf {
  category: SignageAwayCategory;
  // The status label (why they're out, or where they're working) or another
  // office's name; null when the status is private
  label: string | null;
}

export interface SignageAwayPerson {
  person: SignageOccupant;
  teamName: string | null;
  // Null when they're in this office (or not set) for that half
  am: SignageAwayHalf | null;
  pm: SignageAwayHalf | null;
}

export interface SignageAway {
  nameDisplay: SignageNameDisplay;
  refreshIntervalMs: number;
  people: SignageAwayPerson[];
}

// Pairing goes through the shared kiosk pairing endpoints; deviceType SIGNAGE
// means only a signage code can pair this screen.
export function createSignagePairingRequest(code: string): Promise<SignagePairingRequestCreatedResponse> {
  return signageAgent.post('/api/kiosk/pair', { code, deviceType: 'SIGNAGE' });
}

export function pollSignagePairingRequest(requestId: string): Promise<SignagePairingRequestPollResponse> {
  return signageAgent.post('/api/kiosk/pair/poll', { requestId });
}

export function cancelSignagePairingRequestOnServer(requestId: string): Promise<void> {
  return signageAgent.post('/api/kiosk/pair/cancel', { requestId });
}

export function getSignageFloors(): Promise<SignageFloor[]> {
  return signageAgent.get('/api/signage/floors');
}

export function getSignageFloorPlan(floorId: number): Promise<SignageFloorPlan> {
  return signageAgent.get(`/api/signage/floor-plan?floorId=${floorId}`);
}

// Older backends sent safety role holders as plain name strings; newer ones
// send {name, floorId, floorLabel}. A screen can poll across a deploy, so
// tolerate both shapes rather than rendering blank names.
function normaliseSafetyPerson(entry: any): SignageSafetyPerson {
  if (typeof entry === 'string') {
    return { name: entry, floorId: null, floorLabel: null };
  }
  return {
    name: entry?.name ?? '',
    floorId: entry?.floorId ?? null,
    floorLabel: entry?.floorLabel ?? null,
  };
}

export async function getSignageOccupancy(floorId: number): Promise<SignageOccupancy> {
  const occupancy = await signageAgent.get(`/api/signage/occupancy?floorId=${floorId}`);
  if (occupancy?.safety) {
    occupancy.safety.firstAiders = (occupancy.safety.firstAiders ?? []).map(normaliseSafetyPerson);
    occupancy.safety.fireWardens = (occupancy.safety.fireWardens ?? []).map(normaliseSafetyPerson);
  }
  return occupancy;
}

// Everyone whose default office is this one and who isn't in it today.
export function getSignageAway(): Promise<SignageAway> {
  return signageAgent.get('/api/signage/away');
}

export function unpairSignage(): Promise<void> {
  return signageAgent.post('/api/signage/unpair');
}
